import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';

import { getProgressBreakdownDetails } from '../../../../redux/certdetails/action';
import { getMessageKey } from '../../../../utils/utils';
import {
  BY_DECISION,
  CONSULTED,
  DONE,
  PENDING,
  PROGRESS,
  TOTAL,
} from '../constants';
import { customSortByDecision, getIconKind, isEmptyObject } from '../utils';

import DecisionLabel from './DecisionLabel';

import './ViewByDecision.css';

const excludeProperties = [DONE, PENDING, PROGRESS, TOTAL];

function ViewByDecision() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);
  const {
    global: { certKey },
    progressBreakdowndetails: { byDecisionDetails, errorMessage, isLoading },
  } = useSelector((state) => state?.certifications?.details);

  useEffect(() => {
    if (certKey) {
      dispatch(getProgressBreakdownDetails(certKey, BY_DECISION));
    }
  }, [certKey]);

  const refreshProgressDataByDecision = () => {
    if (certKey) {
      dispatch(getProgressBreakdownDetails(certKey, BY_DECISION));
    }
  };

  const showErrorMessage = () => (
    <Box className='ViewByDecision-progressFailure'>
      <Typography kind='body2-bold' className='ViewByDecision-errorMessage'>
        {messages.CERTS_PROGRESS_DECISION_LOAD_ERROR ||
          localize(intl, getMessageKey('CERTS_PROGRESS_DECISION_LOAD_ERROR'))}
      </Typography>
      <Button
        type='button'
        kind='filled'
        size='medium'
        onClick={refreshProgressDataByDecision}>
        {messages['Refresh.ATTESTATIONS'] ||
          localize(intl, getMessageKey('Refresh.ATTESTATIONS'))}
      </Button>
    </Box>
  );

  return (
    <div className='ViewByDecision'>
      {isLoading ? (
        <Box className='ViewByDecision-loader'>
          <Loader kind='dots' format='inline' color='brand' size='small' />
        </Box>
      ) : (
        <>
          {errorMessage && showErrorMessage()}
          {byDecisionDetails?.Summary &&
          !isEmptyObject(byDecisionDetails?.Summary) ? (
            <>
              <div className='ViewByDecision-todoSection'>
                <div className='ViewByDecision-todo'>
                  <Typography kind='body1' className='ViewByDecision-todoCount'>
                    {byDecisionDetails.Summary[PENDING]}
                  </Typography>
                  <Typography
                    kind='body2-bold'
                    className='ViewByDecision-todoLabel'>
                    {messages.CERTS_TODO ||
                      localize(intl, getMessageKey('CERTS_TODO'))}
                  </Typography>
                </div>
              </div>
              <div className='ViewByDecision-divider' />
              <div className='ViewByDecision-doneSection'>
                <div className='ViewByDecision-done'>
                  <Typography kind='body1' className='ViewByDecision-doneCount'>
                    {byDecisionDetails.Summary[DONE]}
                  </Typography>
                  <Typography
                    kind='body2-bold'
                    className='ViewByDecision-doneLabel'>
                    {messages.CERTS_DONE ||
                      localize(intl, getMessageKey('CERTS_DONE'))}
                  </Typography>
                </div>
                <div className='ViewByDecision-doneItems'>
                  {byDecisionDetails &&
                    Object.entries(byDecisionDetails.Summary)
                      .filter(([key]) => !excludeProperties.includes(key))
                      .sort(customSortByDecision)
                      .map(([key, value]) => (
                        <DecisionLabel
                          value={value}
                          iconKind={getIconKind(key)}
                          label={
                            messages[byDecisionDetails?.messageKeys[key]] ||
                            localize(
                              intl,
                              getMessageKey(byDecisionDetails?.messageKeys[key])
                            )
                          }
                          key={key}
                        />
                      ))}
                </div>
              </div>
              {byDecisionDetails?.Summary[CONSULTED] && (
                <>
                  <div className='ViewByDecision-divider' />
                  <div className='ViewByDecision-consulted'>
                    <Typography
                      kind='body1'
                      className='ViewByDecision-consultedCount'>
                      {byDecisionDetails.Summary[CONSULTED]}
                    </Typography>
                    <Typography
                      kind='body2-bold'
                      className='ViewByDecision-consultedLabel'>
                      {messages.CERTS_CONSULTED ||
                        localize(intl, getMessageKey('CERTS_CONSULTED'))}
                    </Typography>
                  </div>
                </>
              )}
            </>
          ) : (
            !errorMessage && showErrorMessage()
          )}
        </>
      )}
    </div>
  );
}

export default ViewByDecision;
